import { styled } from '@linaria/react';
import { Checkbox, Flex, Group, Loader, Modal, Stack, TextInput, Textarea } from '@mantine/core';
import { UseQueryOptions } from '@tanstack/react-query';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import { Initiative } from '../../../api/initiative-client/initiative-client.type';
import { usePageTemplates } from '../../../api/pages-client/pages-client.hooks';
import { PageTemplate, PageTemplateInput } from '../../../api/pages-client/pages-client.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Select } from '../../../ui-library/select/select';
import { Text } from '../../../ui-library/typography/typography';
import { getObjectTypes } from '../dashboards.type';

interface EditDashboardModalProps {
  opened: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description: string;
    pageTemplateId: string | null;
    pageTemplateInputs: PageTemplateInput[];
  }) => void;
  onEdit: (data: { name: string; description: string }) => void;
  initialData: {
    name: string;
    description: string;
  };
  isCreating?: boolean;
}

export const EditDashboardModal: FC<EditDashboardModalProps> = ({
  opened,
  onClose,
  onCreate,
  onEdit,
  initialData,
  isCreating = false,
}) => {
  const { t } = useTranslation();
  const objectTypes = useMemo(() => getObjectTypes(t), [t]);
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const [selectedTemplate, setSelectedTemplate] = useState<PageTemplate | null>(null);
  const [templateInputs, setTemplateInputs] = useState<PageTemplateInput[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [useTemplates, setUseTemplates] = useState(false);

  const portfolios = useGlobalStore((state) => state.portfolios).filter((p) => p.organization?.id === organization?.id);
  const teams = useGlobalStore((state) => state.teams);
  const { pageTemplates, query: pageTemplatesQuery } = usePageTemplates(organization?.id || '', portfolio?.id);
  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);

  const [name, setName] = useState(initialData.name);
  const [description, setDescription] = useState(initialData.description);
  useEffect(() => {
    setName(initialData.name);
    setDescription(initialData.description);
  }, [initialData]);

  // Validate template inputs whenever they or the selected template changes
  useEffect(() => {
    if (selectedTemplate && useTemplates && isCreating) {
      const requiredInputCount = selectedTemplate.config?.inputs?.length || 0;
      const mappedInputCount = templateInputs.length;

      if (requiredInputCount > 0 && mappedInputCount < requiredInputCount) {
        setValidationError(`All template objects must be mapped (${mappedInputCount}/${requiredInputCount} mapped)`);
      } else {
        setValidationError(null);
      }
    } else {
      setValidationError(null);
    }
  }, [selectedTemplate, templateInputs, useTemplates, isCreating]);

  const handleTemplateChange = (templateId: string) => {
    const template = pageTemplates.find((template) => template.id === templateId) || null;
    setSelectedTemplate(template);
    setTemplateInputs([]);
  };

  const handleSave = () => {
    // Additional validation before saving
    if (selectedTemplate && useTemplates && isCreating) {
      const requiredInputs = selectedTemplate.config?.inputs || [];

      // Check if all required inputs have been mapped
      const allInputsMapped = requiredInputs.every((input) =>
        templateInputs.some((mappedInput) => mappedInput.key === input.key && mappedInput.id),
      );

      if (!allInputsMapped) {
        setValidationError('All template objects must be mapped before creating the dashboard');
        return;
      }
    }

    if (isCreating) {
      onCreate({ name, description, pageTemplateId: selectedTemplate?.id || null, pageTemplateInputs: templateInputs });
    } else {
      onEdit({ name, description });
    }
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setUseTemplates(false);
    setSelectedTemplate(null);
    setTemplateInputs([]);
    setValidationError(null);
    onClose();
  };

  const getOptionsByObjectType = (objectType: string) => {
    switch (objectType) {
      case 'portfolio':
        return portfolios.map((p) => ({ label: p.name, value: p.id }));
      case 'project':
        return teams.map((t) => ({ label: t.name, value: t.id }));
      case 'board':
      case 'sprint':
        return teams.flatMap((t) =>
          t.subprojects.map((board) => ({ label: `${t.name} - ${board.name}`, value: board.id })),
        );
      case 'initiative':
        return initiatives?.map((initiative: Initiative) => ({
          value: initiative.id,
          label: initiative.name,
        }));
      default:
        return [];
    }
  };

  const isSaveDisabled = !name || (selectedTemplate && useTemplates && isCreating && validationError !== null);

  return (
    <DashboardModalStyled
      opened={opened}
      onClose={handleClose}
      title={
        <Flex align="center" gap="sm">
          <Text weight="bold" size="large">
            {isCreating ? 'Create New Dashboard' : 'Edit Dashboard'}
          </Text>
        </Flex>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {isCreating && (
          <Group>
            <Checkbox
              label="Use a template"
              checked={useTemplates}
              onChange={(e) => {
                setUseTemplates(e.target.checked);
                setSelectedTemplate(null);
                setTemplateInputs([]);
              }}
              color={secondaryBase}
              disabled={pageTemplatesQuery.isLoading || pageTemplates.length === 0}
            />
          </Group>
        )}
        <TextInput
          label="Dashboard Name"
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
          }}
          required
          placeholder="QA Project Dashboard"
          className="dashboard-input"
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={2}
          placeholder="This dashboard tracks the progress of the QA project."
          className="dashboard-input"
        />
      </Stack>
      {useTemplates && isCreating && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          {pageTemplatesQuery.isLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader />
            </Flex>
          ) : (
            <Group justify="space-between">
              <Text weight="bold" size="medium">
                Template Settings
              </Text>
              <Select
                options={pageTemplates.map((template) => ({ label: template.name, value: template.id }))}
                onChange={(value) => {
                  handleTemplateChange(value);
                }}
                value={selectedTemplate?.id || null}
                placeholder="Select a template"
                style={{ width: '250px' }}
              />
            </Group>
          )}
          <Stack mt="xl">
            {selectedTemplate?.config?.inputs.map((input, index) => {
              const components = selectedTemplate?.config?.components?.filter((component) =>
                component.component_data?.[0].object_ids.includes(input.key),
              );
              // Sprint inputs are surfaced under the localized "Board" label.
              // Branch on the canonical `object_type` (locale-independent) and
              // resolve the replacement through t() so org-level text_config
              // overrides for `common.board` propagate.
              const objectTypeLabel =
                input.object_type === 'sprint'
                  ? t('common.board')
                  : (objectTypes.find((o) => o.value === input.object_type)?.label ?? input.object_type);
              return (
                <Group key={input.key || index} justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text weight="medium">
                      Object {index + 1}: {objectTypeLabel}
                    </Text>
                    <Group gap="xs" style={{ flexWrap: 'wrap' }}>
                      {components.map((component, idx) => (
                        <Fragment key={component.id ?? idx}>
                          {idx !== 0 && <Text size="tiny">|</Text>}
                          <Text size="tiny">{component.name}</Text>
                        </Fragment>
                      ))}
                    </Group>
                  </Stack>
                  <Select
                    options={getOptionsByObjectType(input.object_type) || []}
                    placeholder={`Select a ${objectTypeLabel}`}
                    onChange={(value) => {
                      // Update or add the template input
                      const existingInputIndex = templateInputs.findIndex((t) => t.key === input.key);
                      if (existingInputIndex >= 0) {
                        const updatedInputs = [...templateInputs];
                        updatedInputs[existingInputIndex] = { ...input, id: value };
                        setTemplateInputs(updatedInputs);
                      } else {
                        setTemplateInputs([...templateInputs, { ...input, id: value }]);
                      }
                    }}
                    value={templateInputs.find((t) => t.key === input.key)?.id || null}
                    style={{ minWidth: '200px', flexShrink: 0 }}
                  />
                </Group>
              );
            })}
            {validationError && (
              <Text color="red" size="small" style={{ marginTop: '8px' }}>
                {validationError}
              </Text>
            )}
          </Stack>
        </>
      )}
      <Flex justify="flex-end" gap="sm" mt="xl">
        <Button radius="xl" variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button radius="xl" variant="secondary" onClick={handleSave} disabled={isSaveDisabled === true}>
          {isCreating ? 'Create Dashboard' : 'Save Changes'}{' '}
          <Icon
            style={{ marginLeft: '8px' }}
            name={isCreating ? 'arrow_forward' : 'check_circle'}
            size={16}
            color="white"
          />
        </Button>
      </Flex>
    </DashboardModalStyled>
  );
};

export const DashboardModalStyled = styled(Modal)`
  .mantine-Modal-content {
    border-radius: 12px;
    background: white;
    border: 1px solid #e9ecef;
  }

  .mantine-Modal-header {
    padding: 12px 24px 0 24px;
  }

  .mantine-Modal-body {
    padding: 24px;
  }

  .mantine-Modal-close {
    color: #868e96;
    &:hover {
      background-color: #f1f3f5;
    }
  }

  .mantine-TextInput-input,
  .mantine-Textarea-input {
    border-radius: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: ${secondaryBase};
    }
  }
`;
